import { getOnlineStatus } from './getOnlineStatus';

export type ReservationErrorKind =
  | 'validation'
  | 'required_field'
  | 'invalid_email'
  | 'invalid_phone'
  | 'invalid_headcount'
  | 'booking_window'
  | 'capacity'
  | 'reservation_state'
  | 'permission'
  | 'token_invalid'
  | 'token_expired'
  | 'rate_limit'
  | 'backend_unavailable'
  | 'network'
  | 'offline'
  | 'server'
  | 'unknown';

export type ReservationErrorTranslationKey =
  | `reservation.errors.${ReservationErrorKind}`
  | 'reservation.errors.reservation_not_found'
  | 'reservation.errors.reservation_not_modifiable'
  | 'reservation.errors.past_reservation'
  | 'reservation.errors.reservation_not_available';

export type ReservationErrorPayload = Record<string, unknown> | null | undefined;

export type ReservationErrorMetadata = {
  status?: number;
  backendError?: string;
  backendCode?: string;
  reasonCode?: string;
  remaining?: number;
  capacityRemaining?: number;
  bookingStart?: string;
  bookingEnd?: string;
  source: 'payload' | 'status' | 'message' | 'offline' | 'fallback';
};

export type CanonicalReservationError = {
  kind: ReservationErrorKind;
  translationKey: ReservationErrorTranslationKey;
  metadata: ReservationErrorMetadata;
};

export const RESERVATION_ERROR_TAXONOMY: Record<ReservationErrorKind, string> = {
  validation: 'Malformed or invalid reservation data that is not field-specific.',
  required_field: 'A required reservation field is missing.',
  invalid_email: 'The guest email is missing or invalid.',
  invalid_phone: 'The guest phone number is missing or invalid.',
  invalid_headcount: 'The party size is invalid or outside supported limits.',
  booking_window: 'The requested start or end time is outside the bookable window.',
  capacity: 'The requested date or time window cannot fit the party size.',
  reservation_state: 'The reservation cannot be found, modified, or acted on in its current state.',
  permission: 'The caller is not allowed to perform the reservation action.',
  token_invalid: 'The manage or admin action token is invalid, already used, or malformed.',
  token_expired: 'The manage or admin action token has expired.',
  rate_limit: 'The caller attempted too many reservation operations in a short period.',
  backend_unavailable: 'The backend is temporarily unavailable or timed out.',
  network: 'The request could not be sent due to a network/CORS transport failure.',
  offline: 'The browser is offline.',
  server: 'The backend returned an unexpected server error.',
  unknown: 'No supported reservation error signal matched.',
};

const getString = (payload: ReservationErrorPayload, key: string): string => {
  const value = payload && typeof payload === 'object' ? payload[key] : undefined;
  return typeof value === 'string' ? value : '';
};

const getNumber = (payload: ReservationErrorPayload, key: string): number | undefined => {
  const value = payload && typeof payload === 'object' ? payload[key] : undefined;
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const getPublicTime = (payload: ReservationErrorPayload, key: string): string | undefined => {
  const value = getString(payload, key);
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value) ? value : undefined;
};

const normalizeSignal = (value: string) => value.trim().toUpperCase().replace(/[\s-]+/g, '_');

const equalsSignal = (signal: string, patterns: string[]) => {
  const normalized = normalizeSignal(signal);
  return patterns.some(pattern => normalized === pattern);
};

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/*
 * Exact aliases are for field-level validation codes where false positives are costly
 * (for example a generic "phone support unavailable" message must not become invalid_phone).
 * Compound aliases are limited to stable backend/system codes that may be embedded in
 * legacy wrapper messages, while still requiring non-word/underscore boundaries.
 */
const containsCompoundAlias = (signal: string, patterns: string[]) => {
  const normalized = normalizeSignal(signal);
  return patterns.some(pattern => {
    if (normalized === pattern) return true;
    return new RegExp(`(^|_)${escapeRegExp(pattern)}(_|$)`).test(normalized);
  });
};

const mapSignals = ({
  backendError,
  backendCode,
  reasonCode,
  status,
  source,
  remaining,
  capacityRemaining,
  bookingStart,
  bookingEnd,
}: {
  backendError?: string;
  backendCode?: string;
  reasonCode?: string;
  status?: number;
  source: ReservationErrorMetadata['source'];
  remaining?: number;
  capacityRemaining?: number;
  bookingStart?: string;
  bookingEnd?: string;
}): CanonicalReservationError | null => {
  const signals = [backendError, backendCode, reasonCode].filter((value): value is string => !!value);
  const hasCompound = (patterns: string[]) => signals.some(signal => containsCompoundAlias(signal, patterns));
  const hasExact = (patterns: string[]) => signals.some(signal => equalsSignal(signal, patterns));
  const metadata: ReservationErrorMetadata = {
    status,
    backendError,
    backendCode,
    reasonCode,
    remaining,
    capacityRemaining,
    bookingStart,
    bookingEnd,
    source,
  };

  if (hasCompound(['MANAGE_TOKEN_EXPIRED', 'ADMIN_ACTION_TOKEN_EXPIRED', 'TOKEN_EXPIRED', 'EXPIRED_TOKEN'])) {
    return { kind: 'token_expired', translationKey: 'reservation.errors.token_expired', metadata };
  }
  if (hasCompound(['MANAGE_TOKEN_INVALID', 'ADMIN_ACTION_TOKEN_INVALID', 'TOKEN_INVALID', 'INVALID_TOKEN', 'REPLAYED_TOKEN'])) {
    return { kind: 'token_invalid', translationKey: 'reservation.errors.token_invalid', metadata };
  }
  if (hasCompound(['RATE_LIMIT', 'TOO_MANY_REQUESTS'])) {
    return { kind: 'rate_limit', translationKey: 'reservation.errors.rate_limit', metadata };
  }
  if (hasCompound(['CAPACITY_FULL', 'DAILY_LIMIT_REACHED', 'CAPACITY_LIMITED'])) {
    return { kind: 'capacity', translationKey: 'reservation.errors.capacity', metadata };
  }
  if (hasCompound(['OUTSIDE_BOOKABLE_WINDOW', 'BOOKING_WINDOW', 'BOOKABLE_WINDOW'])) {
    return { kind: 'booking_window', translationKey: 'reservation.errors.booking_window', metadata };
  }
  if (hasCompound(['RESERVATION_NOT_FOUND', 'FOGLALÁS_NEM_TALÁLHATÓ'])) {
    return { kind: 'reservation_state', translationKey: 'reservation.errors.reservation_not_found', metadata };
  }
  if (hasCompound(['PAST_RESERVATION', 'ELMÚLT_IDŐPONTÚ'])) {
    return { kind: 'reservation_state', translationKey: 'reservation.errors.past_reservation', metadata };
  }
  if (hasCompound(['RESERVATION_NOT_MODIFIABLE', 'NOT_MODIFIABLE', 'NEM_MÓDOSÍTHATÓ'])) {
    return { kind: 'reservation_state', translationKey: 'reservation.errors.reservation_not_modifiable', metadata };
  }
  if (hasCompound(['RESERVATION_NOT_AVAILABLE'])) {
    return { kind: 'reservation_state', translationKey: 'reservation.errors.reservation_not_available', metadata };
  }
  if (hasExact(['INVALID_EMAIL', 'EMAIL_REQUIRED', 'EMAIL_MISSING', 'EMAIL_KÖTELEZŐ', 'E_MAIL_KÖTELEZŐ'])) {
    return { kind: 'invalid_email', translationKey: 'reservation.errors.invalid_email', metadata };
  }
  if (hasExact(['INVALID_PHONE', 'PHONE_INVALID', 'PHONE_REQUIRED', 'PHONE_MISSING', 'TELEFONSZÁM_KÖTELEZŐ', 'ÉRVÉNYTELEN_TELEFONSZÁM'])) {
    return { kind: 'invalid_phone', translationKey: 'reservation.errors.invalid_phone', metadata };
  }
  if (hasExact(['INVALID_HEADCOUNT', 'HEADCOUNT_INVALID', 'HEADCOUNT_REQUIRED', 'HEADCOUNT_MISSING', 'LÉTSZÁM_KÖTELEZŐ', 'ÉRVÉNYTELEN_LÉTSZÁM'])) {
    return { kind: 'invalid_headcount', translationKey: 'reservation.errors.invalid_headcount', metadata };
  }
  if (hasExact(['REQUIRED_FIELD', 'MISSING_FIELD', 'FIELD_REQUIRED', 'KÖTELEZŐ_MEZŐ', 'HIÁNYZÓ_MEZŐ'])) {
    return { kind: 'required_field', translationKey: 'reservation.errors.required_field', metadata };
  }
  if (hasExact(['INVALID_REQUEST', 'INVALID_PAYLOAD', 'VALIDATION_ERROR', 'ÉRVÉNYTELEN_KÉRÉS', 'ÉRVÉNYTELEN_ADAT'])) {
    return { kind: 'validation', translationKey: 'reservation.errors.validation', metadata };
  }
  if (hasExact(['PERMISSION_DENIED', 'FORBIDDEN', 'ACCESS_DENIED'])) {
    return { kind: 'permission', translationKey: 'reservation.errors.permission', metadata };
  }

  return null;
};

export const mapReservationError = ({
  response,
  payload,
  error,
  isOnline,
}: {
  response?: Response;
  payload?: ReservationErrorPayload;
  error?: unknown;
  isOnline?: boolean;
}): CanonicalReservationError => {
  const status = response?.status;
  const online = getOnlineStatus(isOnline);
  if (online === false) {
    return { kind: 'offline', translationKey: 'reservation.errors.offline', metadata: { status, source: 'offline' } };
  }

  const payloadError = getString(payload, 'error');
  const payloadCode = getString(payload, 'code');
  const payloadReasonCode = getString(payload, 'reasonCode');
  const remaining = getNumber(payload, 'remaining');
  const capacityRemaining = getNumber(payload, 'capacityRemaining');
  const bookingStart = getPublicTime(payload, 'bookingStart');
  const bookingEnd = getPublicTime(payload, 'bookingEnd');
  if (payloadError || payloadCode || payloadReasonCode) {
    const fromPayload = mapSignals({
      backendError: payloadError,
      backendCode: payloadCode,
      reasonCode: payloadReasonCode,
      status,
      source: 'payload',
      remaining,
      capacityRemaining,
      bookingStart,
      bookingEnd,
    });
    if (fromPayload) return fromPayload;
  }

  if (typeof status === 'number') {
    if (status === 429) return { kind: 'rate_limit', translationKey: 'reservation.errors.rate_limit', metadata: { status, source: 'status' } };
    if (status === 410) return { kind: 'token_expired', translationKey: 'reservation.errors.token_expired', metadata: { status, source: 'status' } };
    if (status === 403) return { kind: 'token_invalid', translationKey: 'reservation.errors.token_invalid', metadata: { status, source: 'status' } };
    if (status === 404) return { kind: 'reservation_state', translationKey: 'reservation.errors.reservation_not_found', metadata: { status, source: 'status' } };
    if (status === 409) return { kind: 'reservation_state', translationKey: 'reservation.errors.reservation_state', metadata: { status, source: 'status' } };
    if (status === 400 || status === 422) return { kind: 'validation', translationKey: 'reservation.errors.validation', metadata: { status, source: 'status' } };
    if ([502, 503, 504].includes(status)) return { kind: 'backend_unavailable', translationKey: 'reservation.errors.backend_unavailable', metadata: { status, source: 'status' } };
    if (status >= 500) return { kind: 'server', translationKey: 'reservation.errors.server', metadata: { status, source: 'status' } };
  }

  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (/failed to fetch|networkerror/i.test(message)) {
    return { kind: 'network', translationKey: 'reservation.errors.network', metadata: { status, backendError: message, source: 'message' } };
  }
  const fromMessage = mapSignals({ backendError: message, status, source: 'message' });
  if (fromMessage) return fromMessage;

  return { kind: 'unknown', translationKey: 'reservation.errors.unknown', metadata: { status, backendError: message || undefined, source: 'fallback' } };
};
